import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import apiClient from '../services/api';
import { 
  LayoutDashboard, FileText, Ticket, User, Settings, LogOut, Bell, Search, Coins, Lock,
  AlertTriangle, Info, X, Loader2, Menu
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Rechnungen', href: '/invoices', icon: FileText, permission: 'invoice.read.tenant' },
  { name: 'Credits', href: '/credits', icon: Coins, permissions: ['credits.read.tenant', 'credits.history.tenant'] },
  { name: 'Tickets', href: '/tickets', icon: Ticket, permission: 'ticket.read.tenant' },
  { name: 'Stammdaten', href: '/profile', icon: User, permissions: ['profile.read.own', 'profile.read.tenant'] },
  { name: 'Admin', href: '/admin', icon: Settings, adminOnly: true },
];

export const MainLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, clearMustChangePassword } = useAuth();
  const { hasPermission, hasAnyPermission, canAccessAdmin } = usePermissions();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);
  const searchTimer = useRef(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const notifRef = useRef(null);

  const mustChangePassword = user?.must_change_password;

  const handleLogout = () => { logout(); navigate('/login'); };
  const handlePasswordChanged = () => { clearMustChangePassword(); setShowPasswordDialog(false); };

  // Close mobile menu on navigation
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Search with debounce
  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.length < 2) { setSearchResults(null); setShowSearch(false); return; }
    setShowSearch(true);
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try { const res = await apiClient.get('/portal/search', { params: { q: value } }); setSearchResults(res.data); }
      catch { setSearchResults(null); }
      finally { setSearchLoading(false); }
    }, 400);
  }, []);

  const handleSearchSelect = (item) => { setShowSearch(false); setSearchQuery(''); navigate(item.url); };

  // Notifications
  const fetchNotifications = useCallback(async () => {
    setNotifsLoading(true);
    try { const res = await apiClient.get('/portal/notifications'); setNotifications(res.data.notifications || []); }
    catch { /* ignore */ }
    finally { setNotifsLoading(false); }
  }, []);

  const toggleNotifs = () => { if (!showNotifs) fetchNotifications(); setShowNotifs(!showNotifs); };

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const filteredNavigation = navigation.filter(item => {
    if (item.adminOnly) return canAccessAdmin();
    if (item.permissions) return hasAnyPermission(item.permissions);
    if (item.permission) return hasPermission(item.permission);
    return true;
  });

  const NavLinks = ({ onClick }) => filteredNavigation.map((item) => {
    const isActive = location.pathname === item.href;
    const Icon = item.icon;
    return (
      <Link key={item.name} to={item.href} onClick={onClick} data-testid={`nav-link-${item.name.toLowerCase()}`}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
        style={{ backgroundColor: isActive ? 'var(--color-primary)' : 'transparent', color: isActive ? 'var(--color-text-on-primary)' : 'var(--color-text-secondary)' }}
        onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'var(--color-background)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}}
        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}}
      >
        <Icon className="w-5 h-5" />{item.name}
      </Link>
    );
  });

  const SearchDropdown = () => showSearch && searchResults && (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto" data-testid="search-results-dropdown">
      {searchResults.total === 0 ? (
        <div className="p-4 text-center text-sm text-slate-500">Keine Ergebnisse</div>
      ) : (
        [{ key: 'invoices', label: 'Rechnungen', icon: FileText, color: 'text-blue-600' },
         { key: 'creditnotes', label: 'Gutschriften', icon: FileText, color: 'text-green-600' },
         { key: 'tickets', label: 'Tickets', icon: Ticket, color: 'text-orange-600' },
         { key: 'users', label: 'Benutzer', icon: User, color: 'text-purple-600' }
        ].map(section => {
          const items = searchResults[section.key] || [];
          if (!items.length) return null;
          const SIcon = section.icon;
          return (
            <div key={section.key}>
              <div className="px-3 py-1.5 text-xs font-medium text-slate-400 uppercase bg-slate-50">{section.label} ({items.length})</div>
              {items.slice(0, 5).map((item, i) => (
                <button key={i} className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 text-left" onClick={() => handleSearchSelect(item)}>
                  <SIcon className={`w-4 h-4 ${section.color} flex-shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{item.title}</p>
                    {item.subtitle && <p className="text-xs text-slate-500 truncate">{item.subtitle}</p>}
                  </div>
                </button>
              ))}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className="flex h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col flex-shrink-0" style={{ backgroundColor: 'var(--color-surface)', borderRight: `1px solid var(--color-border)` }} data-testid="main-sidebar">
        <div className="h-16 flex items-center px-6" style={{ borderBottom: `1px solid var(--color-border)` }}>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>Glasmatic Portal</h1>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto" data-testid="main-navigation"><NavLinks /></nav>
        <div className="p-4" style={{ borderTop: `1px solid var(--color-border)` }}>
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8">
              <AvatarFallback style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{user?.email}</p>
              <p className="text-xs capitalize" style={{ color: 'var(--color-text-secondary)' }}>{user?.global_role || user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 flex flex-col z-50" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="h-16 flex items-center justify-between px-6" style={{ borderBottom: `1px solid var(--color-border)` }}>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>Glasmatic Portal</h1>
              <button onClick={() => setMobileMenuOpen(false)}><X className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} /></button>
            </div>
            <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto"><NavLinks onClick={() => setMobileMenuOpen(false)} /></nav>
            <div className="p-4" style={{ borderTop: `1px solid var(--color-border)` }}>
              <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{user?.email}</p>
              <p className="text-xs capitalize" style={{ color: 'var(--color-text-secondary)' }}>{user?.global_role || user?.role}</p>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="h-14 lg:h-16 flex items-center justify-between px-4 lg:px-8 gap-3" style={{ backgroundColor: 'var(--color-surface)', borderBottom: `1px solid var(--color-border)` }} data-testid="main-header">
          {/* Mobile menu button */}
          <Button variant="ghost" size="icon" className="lg:hidden flex-shrink-0" onClick={() => setMobileMenuOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>

          {/* Search */}
          <div className="flex-1 max-w-md relative" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
              <Input type="search" placeholder="Suchen..." className="pl-10 h-9 text-sm"
                style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)' }}
                data-testid="global-search-input" value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)} onFocus={() => { if (searchResults) setShowSearch(true); }} />
              {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
            </div>
            <SearchDropdown />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2 lg:gap-4 flex-shrink-0">
            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button" onClick={toggleNotifs}>
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: 'var(--color-primary)' }}>{notifications.length}</span>
                )}
              </Button>
              {showNotifs && (
                <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50" data-testid="notifications-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <span className="text-sm font-semibold text-slate-800">Benachrichtigungen</span>
                    <button onClick={() => setShowNotifs(false)}><X className="w-4 h-4 text-slate-400" /></button>
                  </div>
                  {notifsLoading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                   : notifications.length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Keine Benachrichtigungen</div>
                   : <div className="max-h-72 overflow-y-auto">
                      {notifications.map((n) => (
                        <button key={n.id} className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0"
                          onClick={() => { setShowNotifs(false); navigate(n.url); }}>
                          {n.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> : <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />}
                          <div className="min-w-0"><p className="text-sm font-medium text-slate-800">{n.title}</p>{n.message && <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>}</div>
                        </button>
                      ))}
                    </div>}
                </div>
              )}
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium hidden sm:inline max-w-[150px] truncate">{user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mein Konto</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}><User className="mr-2 w-4 h-4" />Profil</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowPasswordDialog(true)} data-testid="change-password-menu"><Lock className="mr-2 w-4 h-4" />Passwort ändern</DropdownMenuItem>
                {canAccessAdmin() && <DropdownMenuItem onClick={() => navigate('/admin')}><Settings className="mr-2 w-4 h-4" />Einstellungen</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button"><LogOut className="mr-2 w-4 h-4" />Abmelden</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8" style={{ backgroundColor: 'var(--color-surface)' }} data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Password Dialogs */}
      <ChangePasswordDialog open={mustChangePassword} onOpenChange={() => {}} forced={true} onSuccess={handlePasswordChanged} />
      <ChangePasswordDialog open={showPasswordDialog && !mustChangePassword} onOpenChange={setShowPasswordDialog} forced={false} onSuccess={handlePasswordChanged} />
    </div>
  );
};
